import {
  Alert,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  MultiSelect,
  Select,
  TagsInput,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PhotoAttachmentsField } from 'src/Components/Forms/PhotoAttachmentsField';
import {
  EMPTY_MEMORY_DATE,
  fromMemoryDate,
  MemoryDateInput,
  MemoryDateValue,
  toMemoryDatePayload,
} from 'src/Components/Memories/MemoryDateInput';
import { MEMORY_KINDS, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { PendingStory, VoiceStoryCapture } from 'src/Components/Memories/VoiceStoryCapture';
import { MemberSelector } from 'src/Components/Shared/MemberSelector';
import { TextInput } from 'src/Components/Shared/TextInput';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { useCreateMemory, useUpdateMemory } from 'src/Hooks/Mutations/useMemoryMutations';
import useFetchCartographyTree from 'src/Hooks/Queries/useFetchCartographyTree';
import useFetchMemory from 'src/Hooks/Queries/useFetchMemory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { memoriesApi } from 'src/Services/API';
import { AssetStorageItemDtoTypeEnum } from 'src/Services/API/generated';
import { MemoryDatePrecision, MemoryKind } from 'src/Services/API/Memories.api';
import {
  trackMemoryCreated,
  trackMemoryRecordingAdded,
  trackMemoryVoiceDraftReceived,
} from 'src/Services/Mixpanel/events';

import styles from './MemoryForm.module.scss';
import { Props } from './MemoryForm.types';

const TITLE_MAX_LENGTH = 80;
const MAX_PHOTOS = 10;
/** Folder of Documents where the photos added to a memory are filed. */
const PHOTOS_FOLDER = '/Photos des souvenirs/';

/** Create or edit a memory on a single screen, like the other drawers. Only the title is required. */
const MemoryForm = ({ memoryId, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const [searchParams] = useSearchParams();
  const isEdit = !!memoryId;

  const { data: memory, isLoading: isLoadingMemory } = useFetchMemory(memoryId);
  const { data: tree } = useFetchCartographyTree();
  const { mutate: createMemory, isLoading: isCreating } = useCreateMemory();
  const { mutate: updateMemory, isLoading: isUpdating } = useUpdateMemory(memoryId ?? '');

  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [date, setDate] = useState<MemoryDateValue>(() => {
    // "Personne n'a encore raconté les années 70" pre-fills the decade.
    const decade = Number(searchParams.get('decade'));
    return decade
      ? { ...EMPTY_MEMORY_DATE, precision: MemoryDatePrecision.Decade, decade }
      : EMPTY_MEMORY_DATE;
  });
  const [kind, setKind] = useState<MemoryKind | null>(null);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [freeNames, setFreeNames] = useState<string[]>([]);
  const [photos, setPhotos] = useState<FormAttachment[]>([]);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [photosError, setPhotosError] = useState<string | null>(null);
  const [place, setPlace] = useState('');
  const [description, setDescription] = useState('');
  const [story, setStory] = useState<PendingStory | null>(null);
  const [isSendingStory, setIsSendingStory] = useState(false);
  const [draftState, setDraftState] = useState<'idle' | 'listening' | 'done' | 'failed'>('idle');
  const [transcript, setTranscript] = useState<string | undefined>();
  const draftedRecording = useRef<Blob | null>(null);

  // Initialised once per memory: the memory is polled while a story is transcribed, and a
  // refetch must not wipe what is being typed.
  useEffect(() => {
    if (!memory) return;
    setTitle(memory.title);
    setDate(fromMemoryDate(memory.takenAt, memory.takenAtPrecision, memory.takenAtApproximate));
    setKind(memory.memoryKind ?? null);
    setRoomIds(memory.rooms.map(room => room.id));
    setMemberIds(memory.people.map(person => person.memberId).filter((id): id is string => !!id));
    setFreeNames(
      memory.people.filter(person => !person.memberId).map(person => person.displayName)
    );
    // Photos added before and photos from Documents alike: the id is the one to send back.
    setPhotos(
      memory.files.map(file => ({
        fileId: file.id,
        name: file.originalName,
        previewUrl: file.previewThumbnailUrl ?? file.previewMediumUrl ?? file.url,
        type: AssetStorageItemDtoTypeEnum.File,
      }))
    );
    setPlace(memory.place ?? '');
    setDescription(memory.description ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memory?.id]);

  // Lucien listens to the story once and fills only the fields still empty: never what the
  // family already typed. Changing the storyteller does not ask him again.
  useEffect(() => {
    const audio = story?.recording.audio;
    if (isEdit || !assetId || !audio || draftedRecording.current === audio) return;
    draftedRecording.current = audio;
    setDraftState('listening');
    setTranscript(undefined);
    memoriesApi
      .draftFromVoice(assetId, {
        audio,
        filename: story.recording.filename.replace('dictation', 'recit'),
        language: i18n.language?.startsWith('en') ? 'en' : 'fr',
      })
      .then(draft => {
        if (draftedRecording.current !== audio) return;
        setTranscript(draft.transcript);
        setTitle(current => (current.trim() ? current : draft.title));
        setDate(current =>
          toMemoryDatePayload(current).takenAt || !draft.takenAt
            ? current
            : fromMemoryDate(draft.takenAt, draft.takenAtPrecision, draft.takenAtApproximate)
        );
        setKind(current => current ?? draft.memoryKind);
        setRoomIds(current => (current.length > 0 ? current : draft.roomIds));
        setMemberIds(current => (current.length > 0 ? current : draft.memberIds));
        setFreeNames(current => (current.length > 0 ? current : draft.freeNames));
        setDescription(current => (current.trim() ? current : draft.description));
        setDraftState('done');
        trackMemoryVoiceDraftReceived({
          has_date: !!draft.takenAt,
          rooms_count: draft.roomIds.length,
          people_count: draft.memberIds.length + draft.freeNames.length,
        });
      })
      .catch(() => {
        if (draftedRecording.current === audio) setDraftState('failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.recording.audio]);

  // Grouped by building: a farm can have 50 rooms.
  const roomGroups = useMemo(
    () =>
      (tree?.buildings ?? [])
        .filter(building => building.rooms.length > 0)
        .map(building => ({
          group: building.name,
          items: building.rooms.map(room => ({ value: room.id, label: room.name })),
        })),
    [tree]
  );

  const kindOptions = MEMORY_KINDS.map(memoryKind => ({
    value: memoryKind,
    label: memoryKindLabel(t, memoryKind),
  }));

  if (isEdit && isLoadingMemory) return <LoadingOverlay visible />;

  const timelinePath = `/asset/${assetId}/memories`;
  const isSaving = isCreating || isUpdating || isSendingStory;

  // Opens the memory in its view drawer, without replaying the drawer animation.
  const openMemory = (id: string, openRecorder = false) =>
    navigate(`${timelinePath}/${id}`, {
      replace: true,
      state: { disableAnimation: true, openRecorder },
    });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const missingTitle = !title.trim();
    setTitleError(
      missingTitle ? t('memories.form.titleRequired', 'Donnez un titre au souvenir.') : null
    );
    setPhotosError(
      photosUploading
        ? t(
            'community.photosStillUploading',
            'Les photos sont encore en cours d’envoi. Réessayez dans quelques secondes.'
          )
        : null
    );
    if (missingTitle || photosUploading) return;

    const payload = {
      title: title.trim(),
      description: description.trim(),
      memoryKind: kind,
      ...toMemoryDatePayload(date),
      fileIds: photos.flatMap(photo => photo.fileId ?? []),
      roomIds,
      people: [
        ...memberIds.map(id => ({ memberId: id })),
        ...freeNames
          .map(name => name.trim())
          .filter(Boolean)
          .map(name => ({ freeName: name })),
      ],
      place: place.trim() ? place.trim() : null,
    };
    const onError = () => {
      notifications.show({
        color: 'red',
        message: t('memories.form.saveError', "Le souvenir n'a pas pu être enregistré. Réessayez."),
      });
    };

    if (isEdit) {
      updateMemory(payload, {
        onSuccess: updated => openMemory(updated.id),
        onError,
      });
      return;
    }
    createMemory(payload, {
      onSuccess: async created => {
        trackMemoryCreated({
          kind: created.memoryKind ?? 'none',
          has_voice: !!story,
          drafted_by_lucien: draftState === 'done',
          source: searchParams.get('source') === 'community' ? 'community' : 'manual',
        });
        if (!story) {
          // No story told yet: the memory opens straight on the microphone.
          openMemory(created.id, true);
          return;
        }
        setIsSendingStory(true);
        try {
          await memoriesApi.addRecording(assetId!, created.id, {
            audio: story.recording.audio,
            filename: story.recording.filename.replace('dictation', 'recit'),
            durationSeconds: story.recording.durationSeconds,
            speakerId: story.speakerId,
            speakerName: story.speakerName,
            transcript,
            language: i18n.language?.startsWith('en') ? 'en' : 'fr',
          });
          trackMemoryRecordingAdded({ duration_seconds: story.recording.durationSeconds });
          notifications.show({
            color: 'teal',
            message: t('memories.form.created', 'Votre souvenir est dans la frise.'),
          });
          openMemory(created.id);
        } catch {
          notifications.show({
            color: 'orange',
            message: t(
              'memories.form.storyNotSent',
              'Le souvenir est enregistré, mais pas le récit. Racontez-le à nouveau depuis sa fiche.'
            ),
          });
          openMemory(created.id, true);
        } finally {
          setIsSendingStory(false);
        }
      },
      onError,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={styles.formStack}>
        {!isEdit && (
          <>
            <VoiceStoryCapture
              label={t('memories.form.voiceLabel', 'Récit à voix haute')}
              onChange={setStory}
            />
            {draftState === 'listening' && (
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">
                  {t('memories.form.lucienListening', 'Lucien écoute votre récit…')}
                </Text>
              </Group>
            )}
            {draftState === 'done' && (
              <Alert color="teal">
                {t(
                  'memories.form.lucienDone',
                  'Lucien a pré-rempli le souvenir à partir de votre récit. Vérifiez et corrigez avant de l’ajouter.'
                )}
              </Alert>
            )}
            {draftState === 'failed' && (
              <Alert color="gray">
                {t(
                  'memories.form.lucienFailed',
                  'Lucien n’a pas pu lire ce récit. Il sera quand même ajouté au souvenir : remplissez le reste à la main.'
                )}
              </Alert>
            )}
          </>
        )}

        <TextInput
          label={t('common.title', 'Titre')}
          placeholder={t('memories.form.titleExample', 'Ex. Le mariage d’Alice et Pierre')}
          withAsterisk
          maxLength={TITLE_MAX_LENGTH}
          value={title}
          error={titleError}
          onChange={event => {
            setTitle(event.currentTarget.value);
            setTitleError(null);
          }}
        />

        <MemoryDateInput value={date} onChange={setDate} />

        <Select
          label={t('memories.form.kindLabel', 'Catégorie')}
          placeholder={t('memories.form.kindPlaceholder', 'Sélectionner une catégorie')}
          data={kindOptions}
          clearable
          value={kind}
          onChange={value => setKind(value as MemoryKind | null)}
        />

        {roomGroups.length > 0 && (
          <MultiSelect
            label={t('memories.form.roomsLabel', 'Pièces')}
            placeholder={t('memories.form.roomsPlaceholder', 'Tapez le nom d’une pièce')}
            data={roomGroups}
            value={roomIds}
            onChange={setRoomIds}
            searchable
            clearable
            hidePickedOptions
            nothingFoundMessage={t('memories.form.noRoom', 'Aucune pièce de ce nom')}
            classNames={{
              wrapper: styles.pillsWrapper,
              input: styles.pillsInput,
              pillsList: styles.pillsList,
            }}
          />
        )}

        <MemberSelector
          label={t('memories.form.membersLabel', 'Membres présents')}
          placeholder={t('memories.form.membersPlaceholder', 'Sélectionner des membres')}
          value={memberIds}
          onChange={setMemberIds}
        />

        <TagsInput
          label={t('memories.form.othersLabel', 'Autres personnes présentes')}
          placeholder={t(
            'memories.form.othersPlaceholder',
            'Ex. Mamie Odette, tonton Jean (Entrée pour ajouter)'
          )}
          value={freeNames}
          onChange={setFreeNames}
          maxLength={100}
          classNames={{
            wrapper: styles.pillsWrapper,
            input: styles.pillsInput,
            pillsList: styles.pillsList,
          }}
        />

        <div className={styles.photos}>
          <div className={styles.photosLabel}>{t('memories.form.photos', 'Photos')}</div>
          <PhotoAttachmentsField
            value={photos}
            onChange={value => {
              setPhotos(value);
              setPhotosError(null);
            }}
            onUploadingChange={setPhotosUploading}
            max={MAX_PHOTOS}
            uploadFolder={PHOTOS_FOLDER}
            sortable
            firstBadge={t('memories.photos.cover', 'Couverture')}
            label={t('memories.photos.add', 'Ajouter des photos')}
            modalTitle={t('memories.photos.modalTitle', 'Ajouter des photos du souvenir')}
            fileUploadLabel={t('memories.photos.uploadLabel', 'Importer des photos')}
            existingFileLabel={t('memories.photos.existingLabel', 'Lier une photo de Documents')}
            error={photosError}
          />
        </div>

        <TextInput
          label={t('memories.form.placeLabel', 'Lieu précis')}
          placeholder={t('memories.form.placeExample', 'Ex. Sous le grand chêne')}
          maxLength={255}
          value={place}
          onChange={event => setPlace(event.currentTarget.value)}
        />

        <Textarea
          label={t('common.description', 'Description')}
          placeholder={t(
            'memories.form.descriptionPlaceholder',
            'Quelques mots écrits, si vous préférez ou en plus du récit'
          )}
          autosize
          minRows={4}
          maxLength={3000}
          value={description}
          onChange={event => setDescription(event.currentTarget.value)}
        />

        <div className={styles.border} />
        <div className={styles.buttons}>
          <Button variant="outline" className="btn-medium" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button variant="primary" type="submit" loading={isSaving}>
            {isEdit ? t('common.save', 'Enregistrer') : t('common.create', 'Créer')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default MemoryForm;
